import { useEffect } from "react";
import { createPortal } from "react-dom";
import AdminChargeSelect from "../../../../common/AdminChargeSelect";
import { calculateNetReceive } from "../utils/pricingCalculations";
import { WEEKDAYS } from "../utils/pricingDefaults";

const blockWheelOnNumberInput = (event) => {
  event.currentTarget.blur();
};

const SeasonalRatePanel = ({
  open,
  isEditing,
  rateTypeTitle,
  startDate,
  endDate,
  rateValues,
  selectedDays,
  isStopSale,
  onClose,
  onApply,
  onDateChange,
  onRateValueChange,
  onDayToggle,
  onAllDaysToggle,
  isSubmitting,
}) => {
  useEffect(() => {
    if (!open) return undefined;

    const scrollY = window.scrollY;
    const previousOverflow = document.body.style.overflow;
    const previousPosition = document.body.style.position;
    const previousTop = document.body.style.top;
    const previousWidth = document.body.style.width;

    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.position = previousPosition;
      document.body.style.top = previousTop;
      document.body.style.width = previousWidth;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[220] flex items-center justify-center p-4 bg-black/40"
      data-modal-overlay="true"
      data-prevent-section-spy="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto overscroll-y-contain"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-gray-900">
              {isEditing ? "Edit" : "Add"} {rateTypeTitle}
            </h3>
            {!isStopSale && startDate && endDate && (
              <p className="text-sm text-gray-500 mt-0.5">
                {startDate} → {endDate}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md hover:bg-gray-100 text-gray-500"
          >
            <i className="fi fi-rr-cross text-sm"></i>
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Start date
              </label>
              <input
                type="date"
                name="startDate"
                min={new Date().toISOString().split("T")[0]}
                value={startDate || ""}
                onChange={onDateChange}
                className="w-full h-9 border rounded-md px-3 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                End date
              </label>
              <input
                type="date"
                name="endDate"
                min={startDate || ""}
                value={endDate || ""}
                onChange={onDateChange}
                className="w-full h-9 border rounded-md px-3 text-sm"
              />
            </div>
          </div>

          {isStopSale && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="text-xs font-medium text-gray-600">
                  Applicable days
                </label>
                <button
                  type="button"
                  onClick={onAllDaysToggle}
                  className="text-xs px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200"
                >
                  {selectedDays.length === WEEKDAYS.length
                    ? "Clear all"
                    : "All days"}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map(({ day, full }) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => onDayToggle(day)}
                    title={full}
                    className={`px-3 py-1 text-xs rounded-full border ${
                      selectedDays.includes(day)
                        ? "bg-red-50 text-red-700 border-red-200"
                        : "bg-gray-50 text-gray-600 border-gray-200"
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!isStopSale && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">Rates per person</p>
              <div className="hidden sm:grid sm:grid-cols-[72px_minmax(0,1fr)_132px_64px] gap-3 px-3 text-xs font-medium text-gray-500">
                <span aria-hidden="true"></span>
                <span>Price</span>
                <span>Admin fee</span>
                <span className="text-right">Net</span>
              </div>
              {[
                { type: "Adult", key: "adult", required: true },
                { type: "Child", key: "child", required: true },
                { type: "Infant", key: "infant", required: false },
              ].map(({ type, key, required }) => (
                <div
                  key={key}
                  className="grid grid-cols-1 sm:grid-cols-[72px_minmax(0,1fr)_132px_64px] gap-x-3 gap-y-2 sm:gap-y-0 p-3 bg-gray-50 rounded-lg items-center"
                >
                  <div className="flex items-center text-sm font-medium">
                    {type}
                    {required && <span className="text-red-500 ml-1">*</span>}
                  </div>
                  <div className="relative min-w-0">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                      ₹
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={rateValues[key]?.basePrice ?? ""}
                      onChange={(e) =>
                        onRateValueChange(key, "basePrice", e.target.value)
                      }
                      onWheel={blockWheelOnNumberInput}
                      className="w-full h-9 border rounded-md pl-7 pr-3 text-sm"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="min-w-0 w-full">
                    <AdminChargeSelect
                      value={rateValues[key]?.adminFee ?? ""}
                      onChange={(value) =>
                        onRateValueChange(key, "adminFee", value)
                      }
                      allowEmpty={key === "infant"}
                      placeholder="Admin fee"
                      className="!h-9 !py-0"
                    />
                  </div>
                  <div className="text-sm text-green-600 font-medium sm:text-right whitespace-nowrap">
                    ₹
                    {calculateNetReceive(
                      rateValues[key]?.basePrice,
                      rateValues[key]?.adminFee
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-100 px-5 py-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-semibold text-white bg-black rounded-md hover:bg-gray-800 disabled:opacity-50"
          >
            {isSubmitting
              ? "Saving…"
              : isStopSale
              ? "Add stop sale"
              : isEditing
              ? "Update rate"
              : "Apply rate"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SeasonalRatePanel;
